<template>
  <div class="page-card">
    <h2 class="page-title">艺人用户</h2>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          placeholder="手机号/昵称/艺名"
          clearable
          style="width: 260px"
        />
        <el-select v-model="query.userStatus" clearable placeholder="状态" style="width: 140px">
          <el-option label="正常" :value="1" />
          <el-option label="封禁" :value="0" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
      </div>
    </div>

    <el-table :data="list" border v-loading="loading">
      <el-table-column prop="artistName" label="艺名" min-width="160" />
      <el-table-column prop="nickname" label="昵称" min-width="140" />
      <el-table-column label="手机号" width="150">
        <template slot-scope="{ row }">
          {{ maskMobile(row.mobile) }}
        </template>
      </el-table-column>
      <el-table-column prop="city" label="城市" width="180" />
      <el-table-column prop="skillTags" label="技能标签" min-width="220" show-overflow-tooltip />
      <el-table-column label="会员" width="130">
        <template slot-scope="{ row }">
          <el-tag :type="membershipTagType(row)">
            {{ membershipText(row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="到期时间" width="150">
        <template slot-scope="{ row }">
          {{ membershipExpireText(row) }}
        </template>
      </el-table-column>
      <el-table-column prop="exposureValue" label="曝光值" width="100" />
      <el-table-column label="状态" width="120">
        <template slot-scope="{ row }">
          <el-tag :type="row.userStatus === 1 ? 'success' : 'danger'">
            {{ row.userStatus === 1 ? '正常' : '封禁' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="180" />
      <el-table-column label="操作" width="320">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openDetail(row)">查看详情</el-button>
          <el-button type="text" @click="handleExposureAdjust(row, 'increase')">增加曝光</el-button>
          <el-button type="text" @click="handleExposureAdjust(row, 'decrease')">减少曝光</el-button>
          <el-button v-if="row.userStatus === 1" type="text" @click="changeStatus(row, 'ban')">封禁</el-button>
          <el-button v-else type="text" @click="changeStatus(row, 'unban')">解封</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page.sync="query.page"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="fetchData"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog title="艺人详情" :visible.sync="detailVisible" width="920px">
      <div v-if="detail.userId">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="艺名">{{ detail.artistName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="昵称">{{ detail.nickname || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ maskMobile(detail.mobile) }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ detail.userStatus === 1 ? '正常' : '封禁' }}</el-descriptions-item>
          <el-descriptions-item label="会员等级">{{ membershipText(detail) }}</el-descriptions-item>
          <el-descriptions-item label="会员到期">{{ membershipExpireText(detail) }}</el-descriptions-item>
          <el-descriptions-item label="曝光值">
            <span>{{ detail.exposureValue || 0 }}</span>
            <el-button type="text" @click="handleExposureAdjust(detail, 'increase')">增加</el-button>
            <el-button type="text" @click="handleExposureAdjust(detail, 'decrease')">减少</el-button>
          </el-descriptions-item>
          <el-descriptions-item label="城市">{{ detail.city || '-' }}</el-descriptions-item>
          <el-descriptions-item label="年龄">{{ detail.age || '-' }}</el-descriptions-item>
          <el-descriptions-item label="身高">{{ detail.heightCm || '-' }}</el-descriptions-item>
          <el-descriptions-item label="体重">{{ detail.weightKg || '-' }}</el-descriptions-item>
          <el-descriptions-item label="技能标签" :span="2">{{ detail.skillTags || '-' }}</el-descriptions-item>
          <el-descriptions-item label="通告偏好" :span="2">{{ detail.noticeTypes || '-' }}</el-descriptions-item>
          <el-descriptions-item label="经验描述" :span="2">{{ detail.experienceDesc || '-' }}</el-descriptions-item>
          <el-descriptions-item label="档期说明" :span="2">{{ detail.scheduleDesc || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-divider>照片资料</el-divider>
        <div v-if="imageMediaList.length" class="media-grid">
          <el-image
            v-for="item in imageMediaList"
            :key="item.id || item.mediaUrl"
            :src="item.mediaUrl"
            :preview-src-list="previewImages"
            fit="cover"
            class="media-image"
          />
        </div>
        <el-empty v-else description="暂无照片资料" :image-size="88" />

        <el-divider>视频资料</el-divider>
        <div v-if="videoMediaList.length" class="media-grid video-grid">
          <div v-for="item in videoMediaList" :key="item.id || item.mediaUrl" class="video-card">
            <video
              class="media-video"
              :src="item.mediaUrl"
              :poster="item.coverUrl || ''"
              controls
              preload="metadata"
            />
            <div class="video-actions">
              <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无视频资料" :image-size="88" />

        <el-divider>星途档案</el-divider>
        <div class="stat-grid">
          <div v-for="item in starOrderStatItems" :key="item.label" class="stat-item">
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
          </div>
        </div>

        <el-table :data="starRecords" border class="star-table">
          <el-table-column label="类型" width="110">
            <template slot-scope="{ row }">{{ row.recordTypeText || recordTypeText(row.recordType) }}</template>
          </el-table-column>
          <el-table-column prop="recordTitle" label="标题" min-width="150" />
          <el-table-column prop="eventDate" label="日期" width="120" />
          <el-table-column prop="partnerName" label="机构/项目方" min-width="140" />
          <el-table-column prop="roleName" label="角色/身份" min-width="120" />
          <el-table-column prop="recordDesc" label="描述" min-width="200" show-overflow-tooltip />
          <el-table-column label="附件" min-width="220">
            <template slot-scope="{ row }">
              <div v-if="starAttachmentItems(row).length" class="star-attachment-grid">
                <div
                  v-for="(item, index) in starAttachmentItems(row)"
                  :key="`${item.mediaUrl}-${index}`"
                  class="star-attachment-item"
                >
                  <el-image
                    v-if="isImageAttachment(item)"
                    :src="item.mediaUrl"
                    :preview-src-list="starImagePreviewList(row)"
                    fit="cover"
                    class="star-attachment-thumb"
                  >
                    <div slot="error" class="star-attachment-error">图片加载失败</div>
                  </el-image>
                  <div v-else-if="isVideoAttachment(item)" class="star-attachment-video-card" @click="openVideoPreview(item)">
                    <video class="star-attachment-video" :src="item.mediaUrl" :poster="item.coverUrl || ''" muted preload="metadata" />
                    <div class="star-attachment-video-mask">
                      <span class="star-attachment-video-play">▶</span>
                      <span>视频</span>
                    </div>
                  </div>
                  <el-link v-else :href="item.mediaUrl" target="_blank" type="primary" class="star-attachment-file">查看附件</el-link>
                </div>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-if="!starRecords.length" description="暂无星途档案" :image-size="88" />

        <el-divider>分销业绩</el-divider>
        <div class="stat-grid distribution-grid">
          <div v-for="item in distributionStatItems" :key="item.label" class="stat-item">
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
          </div>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      title="视频预览"
      :visible.sync="videoPreviewVisible"
      width="960px"
      top="6vh"
      append-to-body
      @closed="handleVideoPreviewClosed"
    >
      <div class="video-preview-wrap">
        <video
          v-if="previewVideo.mediaUrl"
          ref="previewVideoPlayer"
          class="preview-video-player"
          :src="previewVideo.mediaUrl"
          :poster="previewVideo.coverUrl || ''"
          controls
          autoplay
          preload="auto"
        />
      </div>
      <span slot="footer">
        <el-button @click="videoPreviewVisible = false">关闭</el-button>
        <el-button type="primary" @click="requestVideoFullscreen">全屏播放</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  adjustArtistUserExposure,
  banArtistUser,
  getArtistUserDetail,
  getArtistUsers,
  unbanArtistUser
} from '../../../api/platformManage'
import { isImageMedia, isVideoMedia, maskMobile, normalizeMediaItem } from '../../../utils/format'

export default {
  name: 'ArtistUsersPage',
  data() {
    return {
      list: [],
      total: 0,
      loading: false,
      detailVisible: false,
      detail: {},
      query: {
        keyword: '',
        userStatus: undefined,
        page: 1,
        pageSize: 10
      },
      videoPreviewVisible: false,
      previewVideo: {}
    }
  },
  computed: {
    profileMediaList() {
      return (this.detail.mediaList || [])
        .map(item => normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item))
        .filter(Boolean)
    },
    imageMediaList() {
      return this.profileMediaList.filter(item => isImageMedia(item))
    },
    videoMediaList() {
      return this.profileMediaList.filter(item => isVideoMedia(item))
    },
    previewImages() {
      return this.imageMediaList.map(item => item.mediaUrl).filter(Boolean)
    },
    starProfile() {
      return this.detail.starProfile || {}
    },
    starRecords() {
      return this.starProfile.records || []
    },
    starOrderStats() {
      return this.starProfile.orderStats || {}
    },
    starDistributionStats() {
      return this.starProfile.distributionStats || {}
    },
    starOrderStatItems() {
      return [
        { label: '累计接单', value: this.starOrderStats.totalOrderCount || 0 },
        { label: '进行中', value: this.starOrderStats.inProgressOrderCount || 0 },
        { label: '已完成', value: this.starOrderStats.completedOrderCount || 0 },
        { label: '已结算', value: this.starOrderStats.settledOrderCount || 0 },
        { label: '异常关闭', value: this.starOrderStats.closedOrderCount || 0 }
      ]
    },
    distributionStatItems() {
      return [
        { label: '邀请数', value: this.starDistributionStats.totalInviteCount || 0 },
        { label: '有效邀请', value: this.starDistributionStats.validInviteCount || 0 },
        { label: '待审奖励', value: this.starDistributionStats.pendingRewardCount || 0 },
        { label: '已发奖励', value: this.starDistributionStats.grantedRewardCount || 0 },
        { label: '可提现', value: this.starDistributionStats.availableBalance || 0 },
        { label: '冻结', value: this.starDistributionStats.frozenBalance || 0 },
        { label: '累计提现', value: this.starDistributionStats.totalWithdrawAmount || 0 }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    maskMobile,
    resolveUserId(row) {
      return row && row.userId != null ? String(row.userId) : ''
    },
    async fetchData() {
      this.loading = true
      try {
        const data = await getArtistUsers(this.query)
        this.list = data.records || []
        this.total = Number(data.total || 0)
        this.query.page = Number(data.page || this.query.page)
        this.query.pageSize = Number(data.pageSize || this.query.pageSize)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.query.page = 1
      this.fetchData()
    },
    handleSizeChange() {
      this.query.page = 1
      this.fetchData()
    },
    async openDetail(row) {
      this.detail = await getArtistUserDetail(this.resolveUserId(row))
      this.detailVisible = true
    },
    async changeStatus(row, action) {
      const isBan = action === 'ban'
      try {
        await MessageBox.confirm(`确认${isBan ? '封禁' : '解封'}该艺人用户吗？`, '操作确认', {
          type: 'warning',
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      const userId = this.resolveUserId(row)
      if (isBan) {
        await banArtistUser(userId)
      } else {
        await unbanArtistUser(userId)
      }
      this.$message.success(`${isBan ? '封禁' : '解封'}成功`)
      await this.fetchData()
      if (this.detailVisible && String(this.detail.userId) === userId) {
        this.detailVisible = false
        this.detail = {}
      }
    },
    async handleExposureAdjust(row, direction) {
      const isIncrease = direction === 'increase'
      const userId = this.resolveUserId(row)
      if (!userId) return
      const currentExposureValue = Number((row && row.exposureValue) || 0)
      const actionText = isIncrease ? '增加' : '减少'
      const prompt = await MessageBox.prompt(
        `当前曝光值：${currentExposureValue}\n请输入要${actionText}的曝光值`,
        `${actionText}曝光值`,
        {
          confirmButtonText: '确认',
          cancelButtonText: '取消',
          inputValue: '1',
          inputPattern: /^[1-9]\d*$/,
          inputErrorMessage: '请输入大于 0 的整数'
        }
      ).catch(() => null)
      if (!prompt) return
      const amount = Number(prompt.value)
      if (!isIncrease && amount > currentExposureValue) {
        this.$message.warning('减少值不能大于当前曝光值')
        return
      }
      await adjustArtistUserExposure(userId, {
        changeValue: isIncrease ? amount : -amount,
        reason: `${actionText}曝光值`
      })
      this.$message.success(`${actionText}曝光值成功`)
      await this.fetchData()
      await this.refreshDetailIfNeeded(userId)
    },
    async refreshDetailIfNeeded(userId) {
      if (!this.detailVisible || String(this.detail.userId) !== String(userId)) {
        return
      }
      this.detail = await getArtistUserDetail(String(userId))
    },
    openVideoPreview(item) {
      this.previewVideo = {
        mediaUrl: item.mediaUrl || '',
        coverUrl: item.coverUrl || ''
      }
      this.videoPreviewVisible = true
    },
    handleVideoPreviewClosed() {
      const player = this.$refs.previewVideoPlayer
      if (player && typeof player.pause === 'function') {
        player.pause()
        player.currentTime = 0
      }
      this.previewVideo = {}
    },
    async requestVideoFullscreen() {
      const player = this.$refs.previewVideoPlayer
      if (!player) return
      const requestFullscreen =
        player.requestFullscreen ||
        player.webkitRequestFullscreen ||
        player.mozRequestFullScreen ||
        player.msRequestFullscreen
      if (!requestFullscreen) {
        this.$message.warning('当前浏览器不支持全屏播放')
        return
      }
      await requestFullscreen.call(player)
    },
    normalizeStarAttachment(item) {
      if (!item) return null
      return normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item)
    },
    starAttachmentItems(row) {
      return ((row && row.attachments) || [])
        .map(item => this.normalizeStarAttachment(item))
        .filter(Boolean)
    },
    starImagePreviewList(row) {
      return this.starAttachmentItems(row)
        .filter(item => this.isImageAttachment(item))
        .map(item => item.mediaUrl)
    },
    isImageAttachment(item) {
      return isImageMedia(item)
    },
    isVideoAttachment(item) {
      return isVideoMedia(item)
    },
    recordTypeText(type) {
      if (Number(type) === 1) return '成长记录'
      if (Number(type) === 2) return '获奖'
      if (Number(type) === 3) return '合作经历'
      return '其他'
    },
    membershipText(row) {
      if (!row || row.membershipStatus !== 1) return '非会员'
      if (row.membershipLevel === 2) return '高级会员'
      if (row.membershipLevel === 1) return '基础会员'
      return '非会员'
    },
    membershipTagType(row) {
      if (!row || row.membershipStatus !== 1) return 'info'
      return row.membershipLevel === 2 ? 'warning' : 'success'
    },
    membershipExpireText(row) {
      if (!row || row.membershipStatus !== 1) return '-'
      return row.membershipExpireAt || '永久有效'
    }
  }
}
</script>

<style scoped>
.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.video-grid {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}

.media-image {
  width: 100%;
  height: 150px;
  border-radius: 8px;
  background: #f5f7fa;
}

.video-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.media-video {
  width: 100%;
  height: 180px;
  display: block;
  border-radius: 8px;
  background: #111827;
  object-fit: contain;
}

.video-actions {
  display: flex;
  justify-content: flex-end;
}

.video-preview-wrap {
  display: flex;
  justify-content: center;
}

.preview-video-player {
  width: 100%;
  max-height: 72vh;
  border-radius: 10px;
  background: #111827;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.distribution-grid {
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
}

.stat-item {
  padding: 14px 10px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
  text-align: center;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.stat-label {
  margin-top: 6px;
  color: #909399;
}

.star-table {
  margin-top: 12px;
}

.star-attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.star-attachment-item {
  width: 64px;
  height: 64px;
}

.star-attachment-thumb,
.star-attachment-video-card {
  width: 64px;
  height: 64px;
  border-radius: 6px;
}

.star-attachment-thumb {
  background: #f5f7fa;
}

.star-attachment-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 12px;
  text-align: center;
  background: #f5f7fa;
}

.star-attachment-video-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #111827;
}

.star-attachment-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.star-attachment-video-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  font-size: 12px;
  background: rgba(17, 24, 39, 0.42);
}

.star-attachment-video-play {
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 50%;
  text-align: center;
  background: rgba(255, 255, 255, 0.22);
}

.star-attachment-file {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #f5f7fa;
}
</style>
